import { inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { HttpService } from "../http/http.service";
import { JsonDataService } from "../commonData/json-data.service";
import { SessionStorageService } from "ngx-webstorage";
import Swal from "sweetalert2";
import { BehaviorSubject } from "rxjs";
import { SessionKeys } from "../../shared/enums/session-keys.enum";
import { AuthTokenService } from "../auth-token/auth-token.service";

@Injectable({
  providedIn: "root",
})
export class UserService {
  isLdap: any;
  private sessionStorage = inject(SessionStorageService);

  private rightsSubject = new BehaviorSubject<string[]>([]);
  rights$ = this.rightsSubject.asObservable();

  constructor(
    public router: Router,
    public http: HttpService,
    private authToken: AuthTokenService,
    private jsonService: JsonDataService
  ) {
    const savedRights = this.sessionStorage.retrieve(SessionKeys.RightsArray) || [];
    this.rightsSubject.next(savedRights)

    this.jsonService?.getValueFromJSON().then((res: any) => {
      this.isLdap = res.Ldap[0].Value;
    }).catch(err => { });
  }

  setRights(rights: string[]) {
    this.sessionStorage.store(SessionKeys.RightsArray, rights);
    this.rightsSubject.next(rights);
  }

  // ------------------ STORE USER DETAILS ------------------
  // storeUserDetails(userData: any) {
  //   const isUserLoggedIn = true;
  //   this.sessionStorage.store(SessionKeys.IsUserLoggedIn, isUserLoggedIn);
  //   this.sessionStorage.store(SessionKeys.UserId, userData.result[0].UserID);
  //   this.sessionStorage.store(
  //     SessionKeys.UserName,
  //     this.isLdap === 1 ? userData.result[0].UserInitials : userData.result[0].UserName
  //   );
  //   if (this.isLdap === 1) {
  //     this.sessionStorage.store(SessionKeys.WinUserName, userData.result[0].UserName);
  //   }
  //   this.sessionStorage.store(SessionKeys.UserRole, userData.result[0].Role);
  //   this.sessionStorage.store(SessionKeys.Designation, userData.result[0].designation);
  //   this.sessionStorage.store(SessionKeys.UserDepartment, userData.result[0].Department);
  //   this.sessionStorage.store(SessionKeys.UserObject, userData);
  //   this.sessionStorage.store(SessionKeys.Type, userData.result[0].userType);
  //   this.sessionStorage.store(SessionKeys.UserIP, userData.result[0].HostName);
  //   this.sessionStorage.store(SessionKeys.IsLdap, this.isLdap);

  //   const specialRights = userData.result[2].splRights;
  //   const removeRights = userData.result[3].removeRights;

  //   const combinedArray = userData.result[1].rights.concat(specialRights);
  //   let rightsArray = combinedArray.filter((item: any) => removeRights.indexOf(item) < 0);
  //   rightsArray = rightsArray.filter((el: any, i: any, a: any) => i === a.indexOf(el));
  //   this.sessionStorage.store(SessionKeys.RightsArray, rightsArray);

  //   this.storeAllSuperAdminRights();
  // }

  storeUserDetails(userData: any) {
    const isUserLoggedIn = true;
    this.sessionStorage.store(SessionKeys.IsUserLoggedIn, isUserLoggedIn);
    this.sessionStorage.store(SessionKeys.UserId, userData.data.UserID);
    this.sessionStorage.store(SessionKeys.UserRole, userData.data.Role);
    this.sessionStorage.store(SessionKeys.Designation, userData.data?.Designation);
    this.sessionStorage.store(SessionKeys.UserDepartment, userData.data.Department);
    this.sessionStorage.store(SessionKeys.UserObject, userData);
    this.sessionStorage.store(SessionKeys.Type, userData.data.userType);
    this.sessionStorage.store(SessionKeys.UserIP, userData.data.HostName);
    this.sessionStorage.store(SessionKeys.IsLdap, this.isLdap);
    this.sessionStorage.store(SessionKeys.UserSignature, userData.data.signatureFile)

    if (this.isLdap === 1) { // For LDAP System.
      this.sessionStorage.store(SessionKeys.UserName, userData.data.UserInitials);
      this.sessionStorage.store(SessionKeys.WinUserName, userData.data.UserName);
      this.sessionStorage.store(SessionKeys.LastUser, userData.data.UserID);
    } else {                  // For Non-LDAP System.
      this.sessionStorage.store(SessionKeys.UserName, userData.data.UserName);
      this.sessionStorage.store(SessionKeys.LastUser, userData.data.UserID);
    }

    const specialRights = userData.data.splRights;
    const removeRights = userData.data.removeRights;
    const combinedArray = userData.data.rights.concat(specialRights);
    let rightsArray = combinedArray.filter((item: any) => removeRights.indexOf(item) < 0);
    rightsArray = rightsArray.filter((el: any, i: any, a: any) => i === a.indexOf(el));
    this.sessionStorage.store(SessionKeys.RightsArray, rightsArray);
    this.setRights(rightsArray);

    this.storeAllSuperAdminRights();
  }

  storeUserDetailsLdap(userData: any) {
    this.storeUserDetails(userData);
  }

  // ------------------ LOGOUT / CLEAR SESSION ------------------
  private isLoggingOut = false;

  logOut(mode: number) {
    if (this.isLoggingOut) return; // prevent double logout
    this.isLoggingOut = true;

    const userId = this.sessionStorage.retrieve(SessionKeys.UserId);
    const userIP = this.sessionStorage.retrieve(SessionKeys.UserIP);
    const data = { userId, mode: mode === 1 ? "Self" : "Auto", userIP, source: "SOFTWARE" };

    if (mode === 1) {
      this.http.putMethod("login/logOut", data).subscribe({

        next: () => {
          this.authToken.removeTokenFromLocal();
          // localStorage.removeItem('lastRoute');
          Swal.fire("User Logged Out Successfully", "", "success");
          this.clearAllSession();
          this.router.navigate(['authentication/login']).then(() => this.isLoggingOut = false);
        },
        error: () => {
          Swal.fire("Error While Logging Out", "", "warning");
          this.router.navigate(['/home']).then(() => this.isLoggingOut = false);
        },
      });
    } else {
      this.clearAllSession();
      this.router.navigate(['authentication/login']).then(() => {
        window.location.reload();
        this.isLoggingOut = false;
      });
    }
  }

  releaseOnBackButton() {
    const userId = this.sessionStorage.retrieve(SessionKeys.UserId);
    const userIP = this.sessionStorage.retrieve(SessionKeys.UserIP);
    const data = { userId, mode: "NA", userIP, source: "SOFTWARE" };

    this.http.putMethod("login/logOut", data).subscribe({
      next: () => {
        this.authToken.removeTokenFromLocal();
        Swal.fire("", "Login to Continue", "warning");
        this.clearAllSession();
        this.router.navigate(['authentication/login']);
      },
      error: () => {
        Swal.fire("Error While Logging Out", "", "warning");
        this.router.navigate(['/home']);
      },
    });
  }

  logOutonTimeout() {
    const userId = this.sessionStorage.retrieve(SessionKeys.UserId);
    const userIP = this.sessionStorage.retrieve(SessionKeys.UserIP);
    const data = { userId, mode: "Auto", userIP, source: "SOFTWARE" };
    const lastuser = this.sessionStorage?.retrieve(SessionKeys.UserId)


    // Notify backend that session timed out
    this.http.putMethod("login/logOut", data).subscribe(() => { });

    // Clear session data
    this.clearAllSession();

    // ✅ Store the last route before redirecting to login
    const lastRoute = this.router.url;
    this.sessionStorage.store(SessionKeys.LastRoute, lastRoute);
    this.sessionStorage.store(SessionKeys.LastUser, lastuser);

    // Redirect to login page
    this.router.navigate(['authentication/login']);
  }


  private clearAllSession() {
    Object.values(SessionKeys).forEach(key => this.sessionStorage.clear(key));
  }

  // ------------------ RIGHTS ------------------
  getRights() {
    return this.sessionStorage.retrieve(SessionKeys.RightsArray);
  }

  checkRights(right: any) {
    const rightsArray = this.getRights();
    if (!rightsArray.includes(right)) {
      Swal.fire("Access Denied", "", "warning");
      this.router.navigate(["/home"]);
    }
  }

  storeAllSuperAdminRights() {
    const data = { role: "SuperAdmin" };
    this.http.postMethod("role/getRoleRight", data).subscribe({
      next: (res: any) => {
        const items = Object.keys(res.result).map(i => res.result[i].role_rights);
        this.sessionStorage.store(SessionKeys.AllSystemRights, items);
      },
      error: console.log,
    });
  }

  // ------------------ NAVIGATION TOGGLE ------------------
  isOpenSubject = new BehaviorSubject<boolean>(false);
  isOpen$ = this.isOpenSubject.asObservable();
  toggleNavigation() {
    this.isOpenSubject.next(!this.isOpenSubject.getValue());
  }
}
